import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  dataAggregationMethodId,
  type DataAggregationMethodId,
} from './DataAggregationMethod.js'
import { ingestionId, type IngestionId } from './Ingestion.js'
import {
  sensorMeasurementKindId,
  type SensorMeasurementKindId,
} from './SensorMeasurementKind.js'

/** Represents the table public.sensor_reading_ */
export default interface SensorReadingTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<string, string | undefined, string>

  /** Database type: pg_catalog.uuid */
  ingestionId: ColumnType<IngestionId, IngestionId, IngestionId>

  /** Database type: pg_catalog.text */
  loggerSn: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  sensorSn: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  timestamp: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.text */
  dataAggregationMethod: ColumnType<
    DataAggregationMethodId,
    DataAggregationMethodId | undefined,
    DataAggregationMethodId
  >

  /** Database type: pg_catalog.float8 */
  value: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  unit: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  sensorMeasurementKind: ColumnType<
    SensorMeasurementKindId,
    SensorMeasurementKindId,
    SensorMeasurementKindId
  >

  /** Database type: pg_catalog.int4 */
  sensorIndex: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.text */
  measurementOrigin: ColumnType<string, string | undefined, string>

  /** Database type: pg_catalog.timestamptz */
  archivedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.uuid */
  archivedBy: ColumnType<string | null, string | null, string | null>
}

export type SensorReading = Selectable<SensorReadingTable>

export type SensorReadingInitializer = Insertable<SensorReadingTable>

export type SensorReadingMutator = Updateable<SensorReadingTable>

export const sensorReading = z.object({
  id: z.string(),
  ingestionId: ingestionId,
  loggerSn: z.string(),
  sensorSn: z.string(),
  timestamp: z.coerce.date(),
  dataAggregationMethod: dataAggregationMethodId,
  value: z.number(),
  unit: z.string(),
  sensorMeasurementKind: sensorMeasurementKindId,
  sensorIndex: z.number(),
  measurementOrigin: z.string(),
  archivedAt: z.coerce.date().nullable().optional(),
  archivedBy: z.string().nullable().optional(),
}) as unknown as z.Schema<SensorReading>

export const sensorReadingInitializer = z.object({
  id: z.string().optional(),
  ingestionId: ingestionId,
  loggerSn: z.string(),
  sensorSn: z.string(),
  timestamp: z.coerce.date(),
  dataAggregationMethod: dataAggregationMethodId.optional(),
  value: z.number(),
  unit: z.string(),
  sensorMeasurementKind: sensorMeasurementKindId,
  sensorIndex: z.number().optional(),
  measurementOrigin: z.string().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  archivedBy: z.string().optional().nullable(),
}) as unknown as z.Schema<SensorReadingInitializer>

export const sensorReadingMutator = z.object({
  id: z.string().optional(),
  ingestionId: ingestionId.optional(),
  loggerSn: z.string().optional(),
  sensorSn: z.string().optional(),
  timestamp: z.coerce.date().optional(),
  dataAggregationMethod: dataAggregationMethodId.optional(),
  value: z.number().optional(),
  unit: z.string().optional(),
  sensorMeasurementKind: sensorMeasurementKindId.optional(),
  sensorIndex: z.number().optional(),
  measurementOrigin: z.string().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  archivedBy: z.string().optional().nullable(),
}) as unknown as z.Schema<SensorReadingMutator>
